// src/components/consent/PartnerFinancialSharingSection.jsx

import {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Building2,
  CheckCircle2,
  Loader2,
  LockKeyhole,
  RefreshCw,
  ShieldCheck,
  ShieldOff,
} from "lucide-react";

import {
  getMyPartnerFinancialSharingConsents,
  setMyPartnerFinancialSharingConsent,
} from "../../lib/legal/partnerFinancialConsent";

const SHARED_FIELDS = [
  "Monthly sales",
  "Monthly expenses",
  "Calculated profit",
  "Profit margin",
  "Month-end cash balance",
  "Business debt",
  "Employee count",
  "Month-on-month performance changes",
];

const EXCLUDED_FIELDS = [
  "Bank login information",
  "Payment credentials",
  "Gwin password",
  "Authentication credentials",
  "Uploaded documents",
];

function getErrorMessage(error) {
  const message = String(
    error?.message || ""
  ).trim();

  if (
    message
      .toLowerCase()
      .includes("authentication")
  ) {
    return (
      "Your session may have expired. " +
      "Please sign in again."
    );
  }

  return (
    message ||
    "Your partner sharing choices could not be loaded."
  );
}

export default function PartnerFinancialSharingSection() {
  const [
    consents,
    setConsents,
  ] = useState([]);

  const [
    draftByConsentId,
    setDraftByConsentId,
  ] = useState({});

  const [loading, setLoading] =
    useState(true);

  const [refreshing, setRefreshing] =
    useState(false);

  const [savingId, setSavingId] =
    useState("");

  const [error, setError] =
    useState("");

  const [message, setMessage] =
    useState("");

  const loadConsents = useCallback(
    async ({
      background = false,
    } = {}) => {
      if (background) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      setError("");

      try {
        const rows =
          await getMyPartnerFinancialSharingConsents();

        setConsents(rows);

        setDraftByConsentId(
          Object.fromEntries(
            rows.map((row) => [
              row.consent_id,
              row
                .financial_sharing_enabled ===
                true,
            ])
          )
        );
      } catch (loadError) {
        console.error(
          "Partner financial consent loading failed:",
          loadError
        );

        setError(
          getErrorMessage(loadError)
        );
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    []
  );

  useEffect(() => {
    loadConsents();
  }, [loadConsents]);

  function setDraft(
    consentId,
    enabled
  ) {
    setMessage("");
    setError("");

    setDraftByConsentId(
      (current) => ({
        ...current,
        [consentId]:
          enabled === true,
      })
    );
  }

  function resetDraft(consent) {
    setMessage("");
    setError("");

    setDraftByConsentId(
      (current) => ({
        ...current,
        [consent.consent_id]:
          consent
            .financial_sharing_enabled ===
          true,
      })
    );
  }

  async function saveChoice(
    consent
  ) {
    if (
      !consent?.consent_id ||
      savingId
    ) {
      return;
    }

    const nextEnabled =
      draftByConsentId[
        consent.consent_id
      ] === true;

    setSavingId(
      consent.consent_id
    );

    setError("");
    setMessage("");

    try {
      const updated =
        await setMyPartnerFinancialSharingConsent(
          {
            consentId:
              consent.consent_id,

            enabled:
              nextEnabled,
          }
        );

      if (!updated) {
        await loadConsents({
          background: true,
        });

        return;
      }

      setConsents(
        (current) =>
          current.map((row) =>
            row.consent_id ===
            updated.consent_id
              ? updated
              : row
          )
      );

      setDraftByConsentId(
        (current) => ({
          ...current,

          [updated.consent_id]:
            updated
              .financial_sharing_enabled ===
            true,
        })
      );

      setMessage(
        nextEnabled
          ? `Your monthly business performance is now shared with ${updated.partner_name}.`
          : `Your monthly business performance is no longer shared with ${updated.partner_name}.`
      );
    } catch (saveError) {
      console.error(
        "Partner financial consent update failed:",
        saveError
      );

      setError(
        getErrorMessage(saveError)
      );
    } finally {
      setSavingId("");
    }
  }

  return (
    <section className="rounded-3xl border border-[#FFE1CF] bg-[#FFF9F5] p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <ShieldCheck className="h-5 w-5" />
          </div>

          <div>
            <h3 className="text-base font-bold text-[#333333]">
              Partner financial sharing
            </h3>

            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
              Choose whether each active
              programme partner may view
              your detailed monthly
              business check-ins.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={() =>
            loadConsents({
              background: true,
            })
          }
          disabled={
            loading ||
            refreshing ||
            !!savingId
          }
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#E5E5E5] bg-white text-[#757575] transition hover:border-[#FF914D] hover:text-[#FF914D] disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Refresh partner sharing choices"
        >
          <RefreshCw
            className={[
              "h-4 w-4",
              refreshing
                ? "animate-spin"
                : "",
            ].join(" ")}
          />
        </button>
      </div>

      {message && (
        <div
          role="status"
          className="mt-4 flex items-start gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm font-semibold text-green-800"
        >
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />

          <span>{message}</span>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700"
        >
          {error}
        </div>
      )}

      {loading ? (
        <LoadingState />
      ) : consents.length === 0 ? (
        <NoPartnerProgrammes />
      ) : (
        <div className="mt-5 space-y-4">
          {consents.map(
            (consent) => {
              const savedEnabled =
                consent
                  .financial_sharing_enabled ===
                true;

              const draftEnabled =
                draftByConsentId[
                  consent.consent_id
                ] === true;

              const changed =
                savedEnabled !==
                draftEnabled;

              const isSaving =
                savingId ===
                consent.consent_id;

              return (
                <article
                  key={
                    consent.consent_id
                  }
                  className="rounded-2xl border border-[#E5E5E5] bg-white p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FAFAFA] text-[#FF914D]">
                      <Building2 className="h-5 w-5" />
                    </div>

                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <p className="font-bold text-[#333333]">
                            {consent.partner_name ||
                              "Programme partner"}
                          </p>

                          <p className="mt-1 text-xs font-semibold text-[#757575]">
                            Cohort:{" "}
                            {consent.cohort_name ||
                              "Programme cohort"}
                          </p>
                        </div>

                        <span
                          className={[
                            "inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider",
                            savedEnabled
                              ? "bg-green-100 text-green-800"
                              : "bg-[#F3F3F3] text-[#757575]",
                          ].join(" ")}
                        >
                          {savedEnabled
                            ? "Currently shared"
                            : "Not shared"}
                        </span>
                      </div>

                      <div className="mt-4 rounded-2xl bg-[#FAFAFA] p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <p className="text-sm font-bold text-[#333333]">
                              Share monthly
                              business performance
                            </p>

                            <p className="mt-1 text-xs leading-relaxed text-[#757575]">
                              You can change this
                              choice at any time.
                            </p>
                          </div>

                          <button
                            type="button"
                            role="switch"
                            aria-checked={
                              draftEnabled
                            }
                            aria-label={`Share monthly business performance with ${consent.partner_name}`}
                            onClick={() =>
                              setDraft(
                                consent.consent_id,
                                !draftEnabled
                              )
                            }
                            disabled={
                              isSaving
                            }
                            className={[
                              "relative h-7 w-12 shrink-0 rounded-full transition",
                              draftEnabled
                                ? "bg-[#FF914D]"
                                : "bg-[#D8D8D8]",
                              isSaving
                                ? "cursor-not-allowed opacity-60"
                                : "",
                            ].join(" ")}
                          >
                            <span
                              className={[
                                "absolute top-1 h-5 w-5 rounded-full bg-white shadow-sm transition",
                                draftEnabled
                                  ? "left-6"
                                  : "left-1",
                              ].join(" ")}
                            />
                          </button>
                        </div>

                        {changed && (
                          <p className="mt-3 text-xs font-bold text-[#FF914D]">
                            You have an unsaved
                            sharing choice.
                          </p>
                        )}
                      </div>

                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <InformationList
                          title="Information included"
                          icon={ShieldCheck}
                          items={
                            SHARED_FIELDS
                          }
                          positive
                        />

                        <InformationList
                          title="Information not included"
                          icon={LockKeyhole}
                          items={
                            EXCLUDED_FIELDS
                          }
                        />
                      </div>

                      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                        <button
                          type="button"
                          onClick={() =>
                            saveChoice(
                              consent
                            )
                          }
                          disabled={
                            !changed ||
                            isSaving ||
                            !!(
                              savingId &&
                              !isSaving
                            )
                          }
                          className="inline-flex h-11 flex-1 items-center justify-center rounded-2xl bg-[#FF914D] px-5 text-sm font-bold text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          {isSaving ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Saving…
                            </>
                          ) : draftEnabled ? (
                            <>
                              <ShieldCheck className="mr-2 h-4 w-4" />
                              Save and share
                            </>
                          ) : (
                            <>
                              <ShieldOff className="mr-2 h-4 w-4" />
                              Save and stop sharing
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          onClick={() =>
                            resetDraft(
                              consent
                            )
                          }
                          disabled={
                            !changed ||
                            isSaving
                          }
                          className="inline-flex h-11 items-center justify-center rounded-2xl border border-[#E5E5E5] bg-white px-5 text-sm font-bold text-[#757575] hover:bg-[#FAFAFA] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </article>
              );
            }
          )}
        </div>
      )}

      <p className="mt-4 text-[11px] font-semibold leading-relaxed text-[#8A8A8A]">
        Turning this permission off
        prevents the partner dashboard
        from loading your detailed monthly
        financial submissions. Your other
        programme and learning permissions
        are not changed.
      </p>
    </section>
  );
}

function InformationList({
  title,
  icon: Icon,
  items,
  positive = false,
}) {
  return (
    <div className="rounded-2xl border border-[#EFEFEF] bg-white p-4">
      <div className="flex items-center gap-2">
        <Icon
          className={[
            "h-4 w-4",
            positive
              ? "text-green-700"
              : "text-[#757575]",
          ].join(" ")}
        />

        <p className="text-xs font-bold uppercase tracking-wider text-[#757575]">
          {title}
        </p>
      </div>

      <ul className="mt-3 space-y-2">
        {items.map((item) => (
          <li
            key={item}
            className="flex items-start gap-2 text-xs leading-relaxed text-[#555555]"
          >
            <span
              className={[
                "mt-1 h-1.5 w-1.5 shrink-0 rounded-full",
                positive
                  ? "bg-green-600"
                  : "bg-[#BCBCBC]",
              ].join(" ")}
            />

            <span>{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function LoadingState() {
  return (
    <div className="mt-5 flex items-center justify-center rounded-2xl border border-[#EFEFEF] bg-white px-4 py-6 text-sm font-semibold text-[#757575]">
      <Loader2 className="mr-2 h-4 w-4 animate-spin text-[#FF914D]" />

      Loading partner sharing
      choices…
    </div>
  );
}

function NoPartnerProgrammes() {
  return (
    <div className="mt-5 rounded-2xl border border-dashed border-[#DDDDDD] bg-white p-5 text-center">
      <ShieldOff className="mx-auto h-6 w-6 text-[#757575]" />

      <p className="mt-3 text-sm font-bold text-[#333333]">
        No active partner programmes
      </p>

      <p className="mt-1 text-xs leading-relaxed text-[#757575]">
        Partner financial-sharing choices
        will appear here after you join an
        authorised partner cohort.
      </p>
    </div>
  );
}